import * as React from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { List } from "lucide-react";

const currency = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

interface Transaction {
  id: string;
  description: string;
  value: number;
  issueDate?: string | null;
  dueDate?: string | null;
  paymentDate?: string | null;
  settled?: boolean;
  categoryId: string;
  accountId: string;
  paymentMethodId?: string;
  costCenterId?: string;
  supplierId?: string;
}

interface LookupItem {
  id: string;
  name: string;
}

interface ReportTransactionsTableProps {
  transactions: Transaction[];
  categories: LookupItem[];
  accounts: LookupItem[];
  paymentMethods: LookupItem[];
  costCenters: LookupItem[];
  suppliers?: LookupItem[];
  accentColor: string;
  title: string;
  onRowClick?: (transaction: Transaction) => void;
}

export const ReportTransactionsTable: React.FC<ReportTransactionsTableProps> = ({
  transactions,
  categories,
  accounts,
  paymentMethods,
  costCenters,
  suppliers = [],
  accentColor,
  title,
  onRowClick,
}) => {
  const formatDate = (date?: string | null) => {
    if (!date) return "—";
    try {
      return format(parseISO(date), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return "—";
    }
  };

  const lookup = (list: LookupItem[], id?: string) =>
    list.find((i) => i.id === id)?.name || "—";

  const sorted = React.useMemo(
    () =>
      [...transactions].sort((a, b) => {
        const da = a.issueDate || "";
        const db = b.issueDate || "";
        return da.localeCompare(db);
      }),
    [transactions]
  );

  return (
    <Card className="border-2 shadow-lg">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center gap-2">
          <List className="h-6 w-6" style={{ color: accentColor }} />
          {title}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {transactions.length} lançamento{transactions.length !== 1 ? "s" : ""} no período filtrado
          {onRowClick && " · Clique em uma linha para filtrar por categoria"}
        </p>
      </CardHeader>
      <CardContent>
        {transactions.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            Nenhum lançamento encontrado para o período selecionado.
          </p>
        ) : (
          <div className="overflow-auto max-h-[500px] rounded-md border">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-muted">
                <TableRow>
                  <TableHead className="min-w-[100px]">Emissão</TableHead>
                  <TableHead className="min-w-[100px]">Vencimento</TableHead>
                  <TableHead className="min-w-[200px]">Descrição</TableHead>
                  <TableHead className="min-w-[150px]">Categoria</TableHead>
                  <TableHead className="min-w-[130px]">Conta</TableHead>
                  <TableHead className="min-w-[130px]">Forma Pgto</TableHead>
                  <TableHead className="min-w-[130px]">Centro Custo</TableHead>
                  <TableHead className="min-w-[130px]">Fornecedor</TableHead>
                  <TableHead className="min-w-[120px] text-right">Valor</TableHead>
                  <TableHead className="min-w-[90px] text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((t) => (
                  <TableRow
                    key={t.id}
                    className={onRowClick ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""}
                    onClick={() => onRowClick?.(t)}
                  >
                    <TableCell className="whitespace-nowrap">{formatDate(t.issueDate)}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(t.dueDate)}</TableCell>
                    <TableCell className="max-w-[250px] truncate" title={t.description}>
                      {t.description || "—"}
                    </TableCell>
                    <TableCell className="truncate">{lookup(categories, t.categoryId)}</TableCell>
                    <TableCell className="truncate">{lookup(accounts, t.accountId)}</TableCell>
                    <TableCell className="truncate">{lookup(paymentMethods, t.paymentMethodId)}</TableCell>
                    <TableCell className="truncate">{lookup(costCenters, t.costCenterId)}</TableCell>
                    <TableCell className="truncate">{lookup(suppliers, t.supplierId)}</TableCell>
                    <TableCell className="text-right font-semibold whitespace-nowrap">
                      {currency.format(t.value)}
                    </TableCell>
                    <TableCell className="text-center">
                      {t.settled ? (
                        <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-xs">
                          Pago
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/20 text-xs">
                          Aberto
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total row */}
                <TableRow className="bg-muted/50 border-t-2 font-bold">
                  <TableCell colSpan={8} className="text-right">
                    Total
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {currency.format(transactions.reduce((sum, t) => sum + t.value, 0))}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
